import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { UserCheck, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
  DRAFT: "bg-slate-100 text-[#475569]",
  PENDING_APPROVAL: "bg-[#F59E0B]/10 text-[#F59E0B]",
  ACTIVE: "bg-[#10B981]/10 text-[#10B981]",
  PAUSED: "bg-slate-100 text-[#94A3B8]",
  SUSPENDED: "bg-[#EF4444]/10 text-[#EF4444]",
  REJECTED: "bg-[#EF4444]/10 text-[#EF4444]",
};

const ACTIONS_BY_STATUS = {
  PENDING_APPROVAL: ["approve", "reject"],
  ACTIVE: ["pause", "suspend"],
  PAUSED: ["reactivate", "suspend"],
  SUSPENDED: ["reactivate"],
};

export const AdminExperts = () => {
  const [consultants, setConsultants] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/consultants");
      setConsultants(r.data.consultants);
    } catch {
      toast.error("Could not load consultants");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(true);
    try {
      await api.post(`/admin/consultants/${id}/${action}`);
      toast.success(`Consultant ${action}d`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  if (!consultants) return <div className="text-[#94A3B8]">Loading…</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="admin-experts-panel">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <UserCheck className="w-4 h-4 text-[#0EA5E9]" />
          <h2 className="font-display font-extrabold text-[#0B1B3D]">Human Experts</h2>
          <span className="font-mono-kz text-xs text-[#94A3B8]">{consultants.length} total</span>
        </div>
        <button data-testid="experts-refresh-btn" onClick={load} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#475569] border border-slate-200 px-3 py-1.5 rounded-lg hover:text-[#0B1B3D]">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {consultants.length === 0 ? (
        <p className="p-6 text-sm text-[#94A3B8]" data-testid="experts-admin-empty">No consultant profiles yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-experts-table">
            <thead><tr className="text-left font-mono-kz text-xs text-[#94A3B8] uppercase tracking-widest">
              <th className="p-3">Name</th><th className="p-3">Expertise</th><th className="p-3">Country</th><th className="p-3">Status</th><th className="p-3">Stripe</th><th className="p-3">Availability</th><th className="p-3">Price</th><th className="p-3">Actions</th>
            </tr></thead>
            <tbody>
              {consultants.map((c) => (
                <tr key={c.consultant_id} data-testid={`consultant-row-${c.consultant_id}`} className="border-t border-slate-100">
                  <td className="p-3"><p className="font-bold text-[#0B1B3D]">{c.first_name} {c.last_name}</p><p className="font-mono-kz text-xs text-[#94A3B8]">{c.email}</p></td>
                  <td className="p-3 font-mono-kz text-xs text-[#475569] max-w-[160px] truncate">{(c.primary_expertise || []).join(", ")}</td>
                  <td className="p-3 text-[#475569]">{c.country}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold font-mono-kz ${STATUS_STYLES[c.consultant_status]}`}>{c.consultant_status}</span></td>
                  <td className="p-3 font-mono-kz text-xs text-[#94A3B8]">{c.stripe_status}</td>
                  <td className="p-3 font-mono-kz text-xs text-[#475569]">{c.availability}</td>
                  <td className="p-3 font-bold text-[#0B1B3D]">€{c.service?.base_price_eur ?? "—"}</td>
                  <td className="p-3">
                    <div className="flex gap-1.5">
                      {(ACTIONS_BY_STATUS[c.consultant_status] || []).map((a) => (
                        <button key={a} data-testid={`consultant-${a}-${c.consultant_id}`} disabled={busy} onClick={() => act(c.consultant_id, a)}
                          className={`px-2.5 py-1 rounded-md text-xs font-bold border ${a === "approve" || a === "reactivate" ? "border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10" : a === "reject" || a === "suspend" ? "border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10" : "border-slate-200 text-[#475569] hover:border-[#0EA5E9]"}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
